import React, {useEffect, useRef} from 'react';
import {useForm, SubmitHandler} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import ReCAPTCHA from "react-google-recaptcha";
import {useDispatch, useSelector} from 'react-redux';
import {AuthEffects} from '../../store/auth';
import {RootState} from '../../store';
import {LoginFormData} from "../../util/interfaces";
import {Routes} from "../../routes";
import { TextInput, PasswordInput} from "shared-react";
import {
    FormCard,
    FormCardHeader,
    Form,
    StyledField,
    LinkContainer,
    StyledLink,
    SubmitButton,
    Line, StyledA, CaptchaLegal, CaptchaContainer
} from "styles/styled";
import {defaultMessageObj} from "../../util/constants";
import MessageCard from 'components/message-card';
import { AppActions } from "../../store/app";

const schema = yup.object().shape({
    email: yup.string().email('A valid email is required').required('Email is a required field'),
    password: yup.string().required('Password is a required field'),
});

const Login = React.forwardRef(() => {

    const dispatch = useDispatch();
    const urlExtension = useSelector((state:RootState) => state.app.urlExtension);
    const queryParams = useSelector((state:RootState) => state.app.queryParams);
    const captchaKey = useSelector((state: RootState) => state.auth.captchaKey);
    const loginErrorMessage = useSelector((state: RootState) => state.auth.loginErrorMessage);
    const accountErrorMessage = useSelector((state: RootState) => state.account.accountError);
    const captchaDisabled = useSelector((state: RootState) => state.auth.captchaDisabled);
    let recaptchaRef = useRef<ReCAPTCHA>(null);

    const { register, handleSubmit, setValue, formState: { errors } } = useForm({
            resolver: yupResolver(schema),
            mode: 'onSubmit',
            reValidateMode: 'onChange',
        });

    const onSubmit: SubmitHandler<LoginFormData> = async (data: LoginFormData) => {
      if(queryParams.status) {
        dispatch(AppActions.removeParams(['status']));
      }
      let gCaptchaResponse: string | null = null;
      let captchaType: string | undefined;
      if (!captchaDisabled && recaptchaRef.current) {
          gCaptchaResponse = await recaptchaRef.current.executeAsync()
          captchaType = 'invisible'
          recaptchaRef.current.reset();
      }
      dispatch(AuthEffects.startLogin({...data, gCaptchaResponse, captchaType}));
    };

    useEffect(() => {
      queryParams.email && setValue('email', decodeURIComponent(queryParams.email));
    }, [queryParams, setValue]);

    const messageObj = {...defaultMessageObj};
    if (loginErrorMessage) {
      switch (loginErrorMessage) {
        case 'invalid credentials':
          messageObj.message = 'Could not log in. Please review your information and try again.';
          break;
        case 'missing parameters':
          messageObj.message = 'Could not log in. Please review your information and try again.';
          break;
        case 'Rate Limited':
          messageObj.message = <span>You have tried to login too many times in a short period of time. Please contact <StyledA
          aria-label="email support" href="mailto:support@bitpay.com">support</StyledA> if you're having trouble with our login process.</span>;
        break;
        default:
          messageObj.message = 'An unexpected error has occurred. Please try again later.';
      }
    } else if (accountErrorMessage) {
      messageObj.message = accountErrorMessage;
    } else if (queryParams.status) {
      messageObj.message = 'An unexpected error has occurred. Please try again.';
    }

    return (
      <>
        <FormCard>
          <FormCardHeader>Log in to BitPay</FormCardHeader>
          <Line/>
          <MessageCard type={messageObj.type}>
            {messageObj.message}
          </MessageCard>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <StyledField errorMessage={errors.email?.message} error={errors.email} id="default" label="Email Address">
              <TextInput aria-label="Email" type="email" autoComplete="email" readOnly={!!queryParams.email} styleDisabled={!!queryParams.email} {...register('email')}/>
            </StyledField>
            <StyledField errorMessage={errors.password?.message} error={errors.password} id="default" label="Password">
              <PasswordInput aria-label="Password" type="password" autoComplete="current-password" {...register('password')}/>
            </StyledField>
            <CaptchaContainer>
              {!captchaDisabled &&
              <ReCAPTCHA
                sitekey={captchaKey}
                size="invisible"
                badge="inline"
                ref={recaptchaRef}
              />}
            </CaptchaContainer>
            <SubmitButton type="submit" role="button" aria-label="submit">Submit</SubmitButton>
            {!captchaDisabled && <CaptchaLegal>
              This site is protected by reCAPTCHA and the Google
              <StyledA
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noreferrer noopener"
              > Privacy Policy </StyledA>
               and
              <StyledA
                href="https://policies.google.com/terms"
                target="_blank"
                rel="noreferrer noopener"
              > Terms of Service </StyledA>
               apply.
            </CaptchaLegal>}
          </Form>
        </FormCard>
        <LinkContainer>
          <StyledLink style={{marginBottom: '10px'}} aria-label="link to forgot-password page" to={Routes.FORGOT_PASSWORD + urlExtension} role="link">Forgot your password?</StyledLink>
          <p>Don't have an account? <StyledLink aria-label="link to sign up page" to={Routes.SIGNUP + urlExtension} role="link">Sign up</StyledLink></p>
        </LinkContainer>
      </>
    )
});

export default Login;
